import ConsultationNoteController from '../consultation_note_controller'
import mixin                      from '../../support/mixin'
import { isPresent }              from '../../support/helpers'
import ScrollToErrorMixin         from '../../support/scroll_to_error_mixin'

export default class extends mixin(
  ConsultationNoteController,
  ScrollToErrorMixin
) {

  static targets = this.targets.concat([
    'alliedType',
    'alliedOtherText',
    'ampathItems',
    'ampathCategoryItem',
    'ampathCategory',
    'gynaecologistReasonModal',
    'investigationType',
    'investigationOtherText',
    'otherConsultationType',
    'otherItems',
    'pathologyType',
    'referralCategory',
    'referralLetter',
    'sendCopyOfReport',
    'specialistType',
    'specialistOtherText',
    'submitButton'
  ])

  initialize() {
    super.initialize()
    this.originalSubmitButtonText = this.submitButtonText
  }

  // ==== Controllers

  // ==== Actions

  toggleAmpathItems() {
    this.ampathItemsTarget.hidden = !this.#isAmpathSelected()
  }

  toggleOtherItems() {
    this.otherItemsTarget.hidden = this.#isAmpathSelected() ||
      !this.referralTypeFullySpecified()
  }

  toggleSendCopyOfReport(event) {
    this.sendCopyOfReportTarget.hidden = (event.target.value !== this.referralTypeRadiology)
  }

  toggleReferralLetter() {
    this.referralLetterTarget.hidden = this.#isAmpathSelected() ||
      !this.referralTypeFullySpecified()
  }

  showFinalSection(event) {
    if (this.hasAmpathItemsTarget) {
      this.toggleAmpathItems()
    }
    if (this.hasOtherItemsTarget) {
      this.toggleOtherItems()
    }
    this.toggleReferralLetter(event)
  }

  // NOTE: Logic must match app/forms/consultation_note/referral_letter_form.rb
  referralTypeFullySpecified() {
    switch (this.referralCategory) {
      case 'investigation':
        return this.#investigationFullySpecified()
      case 'other_consultation':
        return this.#otherConsultationFullySpecified()
      case 'emergency':
        return true
      default:
        return false
    }
  }

  calculateAmpathItemsCount(event) {
    let updatedValue = 0

    const ampathCategoryItemId = event.target.getAttribute('data-ampath-category-item')
    const ampathCategoryItems  = this.ampathCategoryItemTargets.filter((item) => (
      item.dataset.ampathCategoryItem === ampathCategoryItemId
    ))

    ampathCategoryItems.forEach((item) => {
      if (item.checked) {
        updatedValue += 1
      }
    })

    const ampathCategoryCountField = this.ampathCategoryTargets.filter((item) => (
      item.dataset.ampathCategory === ampathCategoryItemId
    ))[0]

    ampathCategoryCountField.innerHTML = updatedValue
    if (ampathCategoryCountField.classList.contains('u-hide') && updatedValue > 0) {
      ampathCategoryCountField.classList.remove('u-hide')
    } else if (!ampathCategoryCountField.classList.contains('u-hide') && updatedValue === 0) {
      ampathCategoryCountField.classList.add('u-hide')
    }
  }

  openGynaecologistReasonModal() {
    this.gynaecologistReasonModalTarget.classList.remove('u-hide')
  }

  closeGynaecologistReasonModal() {
    this.gynaecologistReasonModalTarget.remove()
  }

  // ==== Getters

  get referralTypeOther() {
    return this.data.get('referralTypeOther')
  }

  get referralTypeGynaecologist() {
    return this.data.get('referralTypeGynaecologist')
  }

  get referralTypePathology() {
    return this.data.get('referralTypePathology')
  }

  get referralTypeRadiology() {
    return this.data.get('referralTypeRadiology')
  }

  get referralTypeGp() {
    return this.data.get('referralTypeGp')
  }

  get originalSubmitButtonText() {
    return this.data.get('originalSubmitButtonText')
  }

  get submitButtonText() {
    return this.submitButtonTarget.textContent
  }

  get referralCategory() {
    return this.referralCategoryTargets.find((target) => target.checked)?.value || null
  }

  get investigationType() {
    return this.investigationTypeTargets.find((target) => target.checked)?.value || null
  }

  get otherConsultationType() {
    return this.otherConsultationTypeTargets.find((target) => target.checked)?.value || null
  }

  get pathologyType() {
    return this.pathologyTypeTargets.find((target) => target.checked)?.value || null
  }

  get specialistType() {
    return this.specialistTypeTargets.find((target) => target.checked)?.value || null
  }

  get alliedType() {
    return this.alliedTypeTargets.find((target) => target.checked)?.value || null
  }

  get investigationOtherText() {
    return this.investigationOtherTextTarget.value
  }

  get specialistOtherText() {
    return this.specialistOtherTextTarget.value
  }

  get alliedOtherText() {
    return this.alliedOtherTextTarget.value
  }

  // ==== Setters

  set submitButtonText(value) {
    this.submitButtonTarget.textContent = value
  }

  set originalSubmitButtonText(value) {
    this.data.set('originalSubmitButtonText', value)
  }

  // ==== Private

  #isAmpathSelected() {
    if (!this.hasPathologyTypeTarget) return false

    return this.referralCategory === 'investigation' &&
      this.investigationType === this.referralTypePathology &&
      this.pathologyType === 'ampath'
  }

  #investigationFullySpecified() {
    if (this.investigationType === this.referralTypeRadiology) {
      return true
    }
    if (this.investigationType === this.referralTypePathology) {
      return this.#pathologyTypeFullySpecified()
    }
    if (this.investigationType === this.referralTypeOther) {
      return isPresent(this.investigationOtherText)
    }
    return false
  }

  #pathologyTypeFullySpecified() {
    if (this.hasPathologyTypeTarget) {
      return isPresent(this.pathologyType)
    }

    return true
  }

  #otherConsultationFullySpecified() {
    if (this.otherConsultationType === 'specialist') {
      return this.#specialistFullySpecified()
    }
    if (this.otherConsultationType === 'allied') {
      return this.#alliedFullySpecified()
    }
    if (this.otherConsultationType === this.referralTypeGp) {
      return true
    }
    return false
  }

  #specialistFullySpecified() {
    if (this.specialistType === this.referralTypeOther) {
      return isPresent(this.specialistOtherText)
    }

    return isPresent(this.specialistType)
  }

  #alliedFullySpecified() {
    if (this.alliedType === this.referralTypeOther) {
      return isPresent(this.alliedOtherText)
    }

    return isPresent(this.alliedType)
  }

  // ==== Channels

  // ==== Callbacks

}
